<template>
  <widget-root-layout
    :root-theme="lpTheme"
    :root-font="lpFont"
    :root-class="{ 'is-scientific': isScientificTheme, 'agenda-widget-container': true }"
    :lp-mode="lpMode"
  >
    <template v-if="isMainViewActive">
      <div :id="anchorParentId" style="position: absolute; top: -5.555rem; left: 0"></div>
      <div
        class="filters-container"
        :class="{ columns: !isScientificTheme, 'is-multiline': !isScientificTheme, 'is-scientific': isScientificTheme }"
        v-if="!showcaseMode"
      >
        <div id="agenda-select" :class="headerColumnClass" v-if="!componentId">
          <agenda-select></agenda-select>
        </div>

        <div id="search-input" :class="headerColumnClass" v-if="!hideSearch && !isLoading">
          <search-input
            :is-text-dark="isPedEvent"
            :placeholder="$t('common.search')"
            v-model="search"
            :button-visible="false"
          ></search-input>
        </div>

        <div id="path-select" :class="pathColumnClass" v-if="allPaths && allPaths.length > 1 && !isLoading">
          <filters-select :hide-paths="hidePaths" @path-selected="resetSearch"></filters-select>
        </div>

        <div id="day-select" :class="daysColumnClass" v-if="!daysSelectionDisabled">
          <day-select class="is-fullwidth" v-if="!hideDays && !isLoading" :is-enabled="!!search" @day-selected="resetSearch"></day-select>
        </div>
      </div>

      <div class="agenda-content">
        <div v-if="error" class="has-text-centered">
          <p class="is-size-4 has-text-weight-bold has-text-danger">
            {{ error | pluckError }}
          </p>
        </div>

        <div v-else class="agenda-sessions">
          <template v-if="search || isScientificTheme">
            <template v-if="(searchResults && searchResults.length) || (!search && isScientificTheme)">
              <agenda-sessions-search-pager-view
                v-bind="sessionsPagerBindings"
                @open-user-modal="$emit('open-user-modal', $event)"
                @open-auth="$emit('open-auth')"
                @open-agenda-session-attendees="$emit('open-agenda-session-attendees', $event)"
                @open-agenda-session-questions="$emit('open-agenda-session-questions', $event)"
                @open-auth-lw-stream="$emit('open-auth-lw-stream', $event)"
              ></agenda-sessions-search-pager-view>
            </template>
            <template v-else>
              <div class="title is-5 has-text-centered">
                {{ $t("common.empty_list_placeholder") }}
              </div>
            </template>
          </template>
          <template v-else-if="hasSessions">
            <agenda-sessions-pager-view
              v-bind="sessionsPagerBindings"
              @open-user-modal="$emit('open-user-modal', $event)"
              @open-auth="$emit('open-auth')"
              @open-agenda-session-attendees="$emit('open-agenda-session-attendees', $event)"
              @open-agenda-session-questions="$emit('open-agenda-session-questions', $event)"
              @open-auth-lw-stream="$emit('open-auth-lw-stream', $event)"
            ></agenda-sessions-pager-view>
          </template>
          <template v-else-if="loadingVisible">
            <list-loading-indicator :active="true"></list-loading-indicator>
          </template>
          <template v-else>
            <p v-if="visibleSessions.length > 0" class="title is-5 has-text-centered">
              <i18n path="agenda.invisible_sessions" tag="span">
                {{ visibleSessions.length }}
              </i18n>
            </p>
            <p v-else class="title is-5 has-text-centered">
              <span>{{ $t("agenda.empty_sessions") }}</span>
            </p>
          </template>
        </div>
      </div>
      <b-modal v-model="sessionDetailsModalActive" :has-modal-card="true" :can-cancel="true" :trap-focus="true">
        <session-details-modal v-bind="sessionDetailsModalProps"></session-details-modal>
      </b-modal>
      <b-modal v-model="sessionRatingModalActive" :has-modal-card="true" :can-cancel="true" :trap-focus="true">
        <agenda-session-rating-modal v-bind="sessionRatingModalProps"></agenda-session-rating-modal>
      </b-modal>
      <b-modal v-model="speakerDetailsModalActive" :has-modal-card="true" :can-cancel="true" :trap-focus="true">
        <speaker-info-modal v-bind="speakerDetailsModalProps"></speaker-info-modal>
      </b-modal>
    </template>

    <template v-else-if="isSpeakerInfoViewActive">
      <speaker-profile-view :speaker="currentView.data.speaker" :event-id="currentView.data.eventId"></speaker-profile-view>
    </template>

    <template v-else-if="isSessionDetailsView">
      <session-details-view
        :key="currentView.data.agendaSessionId"
        :agendaSessionId="currentView.data.agendaSessionId"
      ></session-details-view>
    </template>
  </widget-root-layout>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import DaySelect from "@/widgets/agenda/components/inputs/DaySelect";
import FiltersSelect from "@/widgets/agenda/components/inputs/FiltersSelect";
import AgendaSessionsPagerView from "@/widgets/agenda/components/AgendaSessionsPagerView";
import AgendaSessionsSearchPagerView from "@/widgets/agenda/components/AgendaSessionsSearchPagerView";
import AgendaSelect from "@/widgets/agenda/components/inputs/AgendaSelect";
import SearchInput from "@/shared/components/SearchInput";
import { detectBrowserLanguage } from "@/widgets/shared/utils";
import dayjs from "@/shared/utils/day";
import ListLoadingIndicator from "@/shared/components/common/loading/ListLoadingIndicator";
import Constants from "@/widgets/shared/constants";
import WidgetRootLayout from "@/widgets/shared/components/WidgetRootLayout";
import AgendaSessionRatingModal from "@/widgets/agenda/components/rating/AgendaSessionRatingModal";
import SessionDetailsModal from "@/widgets/agenda/components/sessions/SessionDetailsModal";
import SpeakerInfoModal from "@/shared/components/speakers/SpeakerInfoModal";
import LpConfigConstants from "@/shared/constants/lp-config-constants";
import SpeakerProfileView from "@/widgets/shared/components/SpeakerProfileView";
import SessionDetailsView from "@/widgets/agenda/components/sessions/SessionDetailsView";
import { WidgetViewTypes } from "@/widgets/shared/store/modules/widget-view";

export default {
  name: "App",

  components: {
    WidgetRootLayout,
    ListLoadingIndicator,
    DaySelect,
    FiltersSelect,
    AgendaSelect,
    AgendaSessionsPagerView,
    AgendaSessionsSearchPagerView,
    SearchInput,
    AgendaSessionRatingModal,
    SessionDetailsModal,
    SpeakerInfoModal,
    SpeakerProfileView,
    SessionDetailsView,
  },

  props: {
    theme: {
      type: String,
      default: Constants.WIDGET_THEME_TYPES.DEFAULT,
    },
    slug: {
      type: String,
      required: true,
    },
    componentId: {
      type: Number,
      required: false,
    },
    token: {
      type: String,
      required: false,
    },
    accessCode: {
      type: String,
      required: false,
    },
    locale: {
      type: String,
      required: false,
    },
    lpMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideSessionTime: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDays: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    hidePaths: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLiveIndicator: {
      type: Boolean,
      required: false,
      default: false,
    },
    pageSize: {
      type: Number,
      required: false,
      default: 6,
    },
    hideLiveStreamButtons: {
      type: Boolean,
      required: false,
      default: false,
    },
    componentIdsWithRatingAlwaysEnabled: {
      type: String,
      required: false,
      default: null,
    },
    customSponsors: {
      type: String,
      default: null,
    },
    speakersCountOnPreview: {
      type: Number,
      default: 3,
    },
    highlightedSessionIds: {
      type: String,
      required: false,
      default: null,
    },
    preselectedPathIds: {
      type: String,
      required: false,
      default: null,
    },
    preselectedPlaceIds: {
      type: String,
      required: false,
      default: null,
    },
    preselectedDayId: {
      type: String,
      required: false,
      default: null,
    },
    sessionTimeWithDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    daysSelectionDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    showcaseMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    questionsDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    lpTheme: {
      type: String,
      required: false,
      default: LpConfigConstants.THEME_TYPES.SIMPLE_DAY,
    },
    lpFont: {
      type: String,
      required: false,
      default: LpConfigConstants.FONT_TYPES.NUNITO,
    },
    customCss: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      isLoading: false,
      error: null,
      search: "",
      ongoingTimeout: null,
    };
  },

  beforeMount() {
    const locale = this.locale || detectBrowserLanguage();
    this.$setLocale(locale);
  },

  mounted() {
    this.handleScroll = this.handleScroll.bind(this);
    document.addEventListener("scroll", this.handleScroll);

    this.addCustomStyle();
    this.setAuthorization({
      token: this.token,
      accessCode: this.accessCode,
    });
    this.setConstantComponentId(this.componentId);
    const parsedComponentIdsWithRatingAlwaysEnabled =
      this.componentIdsWithRatingAlwaysEnabled && JSON.parse(this.componentIdsWithRatingAlwaysEnabled);
    this.setComponentIdsWithRatingAlwaysEnabled(parsedComponentIdsWithRatingAlwaysEnabled);
    if (this.locale) {
      this.$setLocale(this.locale);
      this.setCurrentLocale(this.locale);
    }
    this.setTheme(this.theme);
    this.setHideSessionTime(this.hideSessionTime);
    this.setShowLiveIndicator(this.showLiveIndicator);
    this.setHideLiveStreamButtons(this.hideLiveStreamButtons);
    this.setSessionRatings(this.sessionRatings);
    this.setCustomSponsor(this.customSponsors);
    this.setSpeakersCountOnPreview(this.speakersCountOnPreview);
    this.setSessionTimeWithDate(this.sessionTimeWithDate);
    this.setQuestionsDisabled(this.questionsDisabled);
    const parsedHighlightedSessionIds =
      this.highlightedSessionIds && this.highlightedSessionIds.length && JSON.parse(this.highlightedSessionIds);
    if (parsedHighlightedSessionIds) {
      this.setHighlightedSessionIds(parsedHighlightedSessionIds);
    }
    if (this.showcaseMode) {
      const parsedPreselectedPathIds = this.preselectedPathIds && this.preselectedPathIds.length && JSON.parse(this.preselectedPathIds);
      if (parsedPreselectedPathIds) {
        this.setPreselectedPathIds(parsedPreselectedPathIds);
      }
      const parsedPreselectedPlaceIds = this.preselectedPlaceIds && this.preselectedPlaceIds.length && JSON.parse(this.preselectedPlaceIds);
      if (parsedPreselectedPlaceIds) {
        this.setPreselectedPlaceIds(parsedPreselectedPathIds);
      }
      const preselectedDayId = this.preselectedDayId && Number.parseInt(this.preselectedDayId);
      if (preselectedDayId) {
        this.setPreselectedDayId(preselectedDayId);
      }
    }
    this.requestEventLoad();
    this.startTimeUpdates();
  },

  beforeDestroy() {
    this.stopTimeUpdates();
  },

  methods: {
    ...mapMutations([
      "setAuthorization",
      "setLpMode",
      "setConstantComponentId",
      "setComponentIdsWithRatingAlwaysEnabled",
      "setHighlightedSessionIds",
      "updateCurrentTime",
      "setRequestRefreshToken",
      "setCurrentLocale",
      "setTheme",
      "setHideSessionTime",
      "setShowLiveIndicator",
      "setHideLiveStreamButtons",
      "setSessionRatings",
      "setCustomSponsor",
      "setSpeakersCountOnPreview",
      "setSessionTimeWithDate",
      "setPreselectedDayId",
      "setPreselectedPathIds",
      "setPreselectedPlaceIds",
      "setQuestionsDisabled",
    ]),
    ...mapActions("agendas", ["selectAgendaById"]),
    ...mapActions(["initEvent", "deepReloadData"]),
    ...mapActions("sessions", ["loadSessions"]),
    ...mapMutations("paths", ["setPaths"]),
    ...mapMutations("places", ["setPlaces"]),
    ...mapActions("days", ["setDays"]),
    ...mapMutations("lpNotifications", ["clearNotification"]),
    ...mapMutations("modals", ["closeSessionDetailsModal", "closeSessionRatingModal", "closeSpeakerDetailsModal"]),
    ...mapActions("widgetView", ["setScrollTopPos"]),

    handleScroll(event) {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      this.setScrollTopPos({ top: scrollPosition, componentId: this.$root.componentId });
    },

    addCustomStyle() {
      if (this.customCss) {
        const widgetContent = this.$el;
        const styleElement = document.createElement("style");
        styleElement.textContent = this.customCss;
        widgetContent.appendChild(styleElement);
      }
    },

    async requestEventLoad() {
      if (!this.hasSessions) {
        this.isLoading = true;
      }

      this.error = null;

      try {
        await this.initEvent({ eventSlug: this.slug });
      } catch (err) {
        const errors = err.response.data.errors;
        if (errors && errors.length) {
          if (errors[0].code === Constants.REST_TOKEN_EXPIRED) {
            this.$emit("request-refresh-token");
          } else {
            this.error = err;
          }
        } else {
          this.error = err;
        }
      } finally {
        this.isLoading = false;
        if (this.componentId) {
          this.selectAgendaById(this.componentId);
        }
      }
    },

    resetSearch() {
      this.search = "";
    },

    startTimeUpdates() {
      this.timeUpdatesRunning = true;
      this.nextUpdate();
    },

    stopTimeUpdates() {
      this.timeUpdatesRunning = false;
      clearTimeout(this.ongoingTimeout);
    },

    nextUpdate() {
      if (this.timeUpdatesRunning) {
        this.updateCurrentTime();
        const currentTime = new Date();
        const nextMinute = new Date(currentTime.getTime());
        nextMinute.setMinutes(nextMinute.getMinutes() + 1, 1, 0);
        this.ongoingTimeout = setTimeout(() => this.nextUpdate(), nextMinute.getTime() - currentTime.getTime());
      }
    },
  },

  computed: {
    ...mapState(["timezone", "eventColors", "requestRefreshToken", { currentLocale: "locale" }]),
    ...mapGetters(["visibleSessions", "filteredSessionsWithMeetings", "searchSessions", "showcaseSessions"]),
    ...mapGetters("sessions", [
      "isLoadingSessionsFromComponent",
      "pathsFromCurrentComponent",
      "placesFromCurrentComponent",
      "daysFromCurrentComponent",
    ]),
    ...mapState("agendas", ["selectedId"]),
    ...mapGetters("agendas", ["activeAgendaRatings"]),
    ...mapGetters("paths", ["allPaths"]),
    ...mapGetters("lpNotifications", ["notificationToShow"]),
    ...mapGetters("modals", [
      "sessionDetailsModalVisible",
      "sessionRatingModalVisible",
      "speakerDetailsModalVisible",
      "sessionDetailsModalProps",
      "sessionRatingModalProps",
      "speakerDetailsModalProps",
    ]),
    ...mapGetters("widgetView", ["activeView"]),

    dayjs: () => dayjs,

    currentView() {
      return this.activeView(this.$root.componentId);
    },

    isMainViewActive() {
      return this.currentView.view === WidgetViewTypes.MAIN;
    },

    isSpeakerInfoViewActive() {
      return this.currentView.view === WidgetViewTypes.SPEAKER_INFO;
    },

    isSessionDetailsView() {
      return this.currentView.view === WidgetViewTypes.SESSION_DETAILS;
    },

    hasSessions() {
      return this.filteredSessionsWithMeetings.length > 0;
    },

    searchResults() {
      return this.searchSessions(this.search);
    },

    isFetchingSessions() {
      return this.isLoadingSessionsFromComponent(this.selectedId);
    },

    loadingVisible() {
      return this.isLoading || this.isFetchingSessions;
    },

    isPedEvent() {
      return this.slug.includes("pro-experts-days");
    },

    sessionsPagerBindings() {
      let sessions =
        (this.showcaseMode && this.showcaseSessions) ||
        (this.search && this.search.length && this.searchResults) ||
        (this.isScientificTheme && this.filteredSessionsWithMeetings.map(it => it.value)) ||
        this.filteredSessionsWithMeetings;
      return {
        sessions: sessions,
        hideDays: this.hideDays,
        hideSessionTime: this.hideSessionTime,
        showLiveIndicator: this.showLiveIndicator,
        pageSize: this.pageSize,
        hideLiveStreamButtons: this.hideLiveStreamButtons,
        sessionRatings: this.activeAgendaRatings,
        customSponsors: this.customSponsors,
        searchQuery: this.search,
        parentId: this.anchorParentId,
      };
    },

    headerColumnClass() {
      if (this.isScientificTheme) {
        return "";
      }
      let columnsCount = 0;
      if (!this.hideSearch) {
        columnsCount++;
      }
      if (!this.componentId) {
        columnsCount++;
      }
      if (this.allPaths && this.allPaths.length > 1) {
        if (columnsCount === 2) {
          return "column is-one-third";
        } else {
          return "column is-three-fifths";
        }
      } else {
        if (columnsCount === 2) {
          return "column is-half";
        } else {
          return "column is-full";
        }
      }
    },

    pathColumnClass() {
      if (this.isScientificTheme) {
        return "";
      }
      if (this.allPaths && this.allPaths.length > 1) {
        let columnsCount = 0;
        if (!this.hideSearch) {
          columnsCount++;
        }
        if (!this.componentId) {
          columnsCount++;
        }
        if (columnsCount === 2) {
          return "column is-one-third";
        } else if (columnsCount === 1) {
          return "column is-two-fifth";
        } else {
          return "column is-full";
        }
      } else {
        return "";
      }
    },

    daysColumnClass() {
      if (this.isScientificTheme) {
        return "";
      } else {
        return "column is-full";
      }
    },

    parsedComponentIdsWithRatingAlwaysEnabled() {
      return this.componentIdsWithRatingAlwaysEnabled && JSON.parse(this.componentIdsWithRatingAlwaysEnabled);
    },

    anchorParentId() {
      return `scroll-anchor-${this.componentId}`;
    },

    isScientificTheme() {
      return this.theme === Constants.WIDGET_THEME_TYPES.SCIENTIFIC;
    },

    sessionDetailsModalActive: {
      set(newValue) {
        if (!newValue) {
          this.closeSessionDetailsModal();
        }
      },

      get() {
        return this.sessionDetailsModalVisible;
      },
    },

    sessionRatingModalActive: {
      set(newValue) {
        if (!newValue) {
          this.closeSessionRatingModal();
        }
      },

      get() {
        return this.sessionRatingModalVisible;
      },
    },

    speakerDetailsModalActive: {
      set(newValue) {
        if (!newValue) {
          this.closeSpeakerDetailsModal();
        }
      },

      get() {
        return this.speakerDetailsModalVisible;
      },
    },
  },

  watch: {
    theme(theme) {
      this.setTheme(theme);
    },

    timezone(timezone) {
      dayjs.tz.setDefault(timezone);
    },

    token(newVal, oldVal) {
      this.setAuthorization({
        token: newVal,
        accessCode: this.accessCode,
      });
      if ((!oldVal && newVal) || (oldVal && !newVal)) {
        setTimeout(() => {
          this.requestEventLoad();
        }, 1000);
      }
    },

    eventColors(newVal) {
      if (!this.lpMode && this.$setThemeColors) {
        this.$setThemeColors(newVal);
      }
    },

    requestRefreshToken(newVal) {
      if (newVal) {
        this.$emit("request-refresh-token");
        this.setRequestRefreshToken(false);
      }
    },

    locale(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        this.$setLocale(newVal);
        this.setCurrentLocale(newVal);
        this.deepReloadData({ eventSlug: this.slug });
      }
    },

    lpMode: {
      immediate: true,
      handler: function (newVal) {
        this.setLpMode(newVal);
      },
    },

    componentId: {
      immediate: false,
      handler: function (newVal) {
        this.setConstantComponentId(newVal);
        this.requestEventLoad();
      },
    },

    pathsFromCurrentComponent: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) {
          const parsedPreselectedPathIds = this.preselectedPathIds && this.preselectedPathIds.length && JSON.parse(this.preselectedPathIds);
          if (parsedPreselectedPathIds) {
            this.setPaths({ paths: newValue, preselectedPathIds: parsedPreselectedPathIds });
          } else {
            this.setPaths({ paths: newValue, preselectedPathIds: [] });
          }
        }
      },
    },

    placesFromCurrentComponent: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) {
          const parsedPreselectedPlacesIds =
            this.preselectedPlaceIds && this.preselectedPlaceIds.length && JSON.parse(this.preselectedPlaceIds);
          if (parsedPreselectedPlacesIds) {
            this.setPlaces({ places: newValue, preselectedPlaceIds: parsedPreselectedPlacesIds });
          } else {
            this.setPlaces({ places: newValue, preselectedPlaceIds: [] });
          }
        }
      },
    },

    daysFromCurrentComponent: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) {
          if (this.preselectedDayId) {
            this.setDays({ days: newValue, preselectedDayId: Number.parseInt(this.preselectedDayId) });
          } else {
            this.setDays({ days: newValue, preselectedDayId: null });
          }
        }
      },
    },

    notificationToShow: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) {
          this.$emit("show-notification", newValue);
          this.clearNotification();
        }
      },
    },

    questionsDisabled: {
      immediate: false,
      handler: function (newValue) {
        this.setQuestionsDisabled(newValue);
      },
    },
  },
};
</script>

<style lang="scss">
html {
  text-align: left;
}
</style>
